import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography, useTheme, SxProps, Theme } from '@mui/material';
import { buildJsonPath, useJsonObjectViewerExpansion } from './useJsonObjectViewerExpansion';
import { useJsonObjectViewerStringExpansion } from './useJsonObjectViewerStringExpansion';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export type JsonObjectViewerProps = {
  data: unknown;
  defaultExpandedDepth?: number;
  defaultWidth?: number | 'auto';
  defaultHeight?: number | 'auto';
  minWidth?: number;
  minHeight?: number;
  maxTruncatedStringChars?: number;
  sx?: SxProps<Theme>;
};

const TriangleArrow = ({
  direction,
  color,
}: {
  direction: 'right' | 'down';
  color: string;
}) => {
  if (direction === 'right') {
    return (
      <Box
        sx={{
          width: 0,
          height: 0,
          borderTop: '5px solid transparent',
          borderBottom: '5px solid transparent',
          borderLeft: `7px solid ${color}`,
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: 0,
        height: 0,
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTop: `7px solid ${color}`,
      }}
    />
  );
};

const isExpandable = (value: unknown) => {
  if (value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length > 0;
  return false;
};

const formatPrimitive = (value: unknown) => {
  if (value === undefined) return 'undefined';
  return JSON.stringify(value, null, 0);
};

const MAX_TRUNCATED_STRING_CHARS = 100;

const getTruncatedString = (
  value: string,
  maxChars = MAX_TRUNCATED_STRING_CHARS,
) => {
  if (!value) {
    return {
      text: value,
      isTruncated: false,
    };
  }

  const charTruncated = value.length > maxChars;
  const truncatedText = charTruncated ? `${value.slice(0, maxChars)}…` : value;

  return {
    text: truncatedText,
    isTruncated: charTruncated,
  };
};

const JsonObjectViewer: React.FC<JsonObjectViewerProps> = ({
  data,
  defaultExpandedDepth = 6,
  defaultWidth = 'auto',
  defaultHeight = 'auto',
  minWidth = 220,
  minHeight = 0,
  maxTruncatedStringChars = MAX_TRUNCATED_STRING_CHARS,
  sx,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [viewerWidth, setViewerWidth] = useState<number | 'auto'>(defaultWidth);
  const [viewerHeight, setViewerHeight] = useState<number | 'auto'>(defaultHeight);
  const [isResizing, setIsResizing] = useState(false);
  const [isResizeHandleHovered, setIsResizeHandleHovered] = useState(false);
  const [copiedValuePath, setCopiedValuePath] = useState<string | null>(null);
  const copiedResetTimeoutRef = useRef<number | null>(null);
  const resizeStartRef = useRef({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const { isExpanded, toggleExpanded } = useJsonObjectViewerExpansion({
    data,
    defaultExpandedDepth,
  });
  const { isStringExpanded, toggleStringExpanded } = useJsonObjectViewerStringExpansion();

  const fontFamily = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
  const backgroundColor =
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f3f4f6';
  const borderColor =
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e5e7eb';
  const keyColor = theme.palette.mode === 'dark' ? '#cbd5e1' : '#475569';
  // Theme-based colors
  const arrowColor = theme.palette.primary.main;
  const valueColor = theme.palette.mode === 'dark' ? '#e5e7eb' : '#0f172a'; // fallback
  const stringColor = theme.palette.secondary.main;
  const numberColor = theme.palette.mode === 'dark' ? '#ffd39a' : '#b45309';
  const booleanColor = theme.palette.mode === 'dark' ? '#d2b5ff' : '#6d28d9';
  const nullColor = theme.palette.mode === 'dark' ? '#ff9da1' : '#b91c1c';
  const mutedColor = theme.palette.mode === 'dark' ? 'rgba(229,231,235,0.8)' : '#64748b';
  // Preview keys should be noticeably lighter than expandable header keys.
  const previewKeyColor = theme.palette.mode === 'dark' ? 'rgba(203,213,225,0.55)' : 'rgba(71,85,105,0.45)';
  // Preview should be uniform light color for keys + values.
  const previewValueColor = previewKeyColor;
  // Only arrow + key/value colors matter (neutralize background/borders).
  const neutralBg = 'transparent';
  const neutralBorder = 'transparent';
  const canResizeWidth = viewerWidth !== 'auto';
  // Height can start from auto (content-sized) and become resizable on first drag.
  const canResizeHeight = viewerHeight !== 'auto' || defaultHeight === 'auto';
  const showResizeHandle = canResizeWidth || canResizeHeight;

  useEffect(() => {
    setViewerWidth(defaultWidth);
  }, [defaultWidth]);

  useEffect(() => {
    setViewerHeight(defaultHeight);
  }, [defaultHeight]);

  useEffect(() => {
    return () => {
      if (copiedResetTimeoutRef.current !== null) {
        window.clearTimeout(copiedResetTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const deltaX = event.clientX - resizeStartRef.current.x;
      const deltaY = event.clientY - resizeStartRef.current.y;

      if (canResizeWidth) {
        setViewerWidth(Math.max(minWidth, resizeStartRef.current.width + deltaX));
      }

      if (canResizeHeight) {
        setViewerHeight(Math.max(minHeight, resizeStartRef.current.height + deltaY));
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, canResizeWidth, canResizeHeight, minWidth, minHeight]);

  const handleResizeStart = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!showResizeHandle || !containerRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const currentContainerHeight = containerRef.current.offsetHeight;
      const contentHeight = contentRef.current?.scrollHeight ?? currentContainerHeight;
      const baseHeight = viewerHeight === 'auto'
        ? Math.max(minHeight, contentHeight)
        : Math.max(minHeight, currentContainerHeight);

      resizeStartRef.current = {
        x: event.clientX,
        y: event.clientY,
        width: containerRef.current.offsetWidth,
        height: canResizeHeight ? baseHeight : currentContainerHeight,
      };

      // Initialize from content height only in auto mode; keep user-resized height otherwise.
      if (canResizeHeight && viewerHeight === 'auto') {
        setViewerHeight(baseHeight);
      }

      setIsResizing(true);
    },
    [showResizeHandle, viewerHeight, canResizeHeight, minHeight],
  );

  const renderPrimitiveValue = (value: unknown, opts?: { preview?: boolean; valuePath?: string }) => {
    const { preview = false } = opts ?? {};
    const fontSize = preview ? '11px' : '12px';
    const valuePath = opts?.valuePath;

    const handleCopyValue = (valueToCopy: string, path?: string) => {
      if (!path) {
        return;
      }

      navigator.clipboard
        .writeText(valueToCopy)
        .then(() => {
          setCopiedValuePath(path);
          if (copiedResetTimeoutRef.current !== null) {
            window.clearTimeout(copiedResetTimeoutRef.current);
          }
          copiedResetTimeoutRef.current = window.setTimeout(() => {
            setCopiedValuePath(currentPath => (currentPath === path ? null : currentPath));
            copiedResetTimeoutRef.current = null;
          }, 3000);
        })
        .catch(() => {
          // Silently ignore clipboard errors to avoid breaking viewer interactions.
        });
    };

    const renderValueWithCopy = (displayNode: React.ReactNode, valueToCopy: string) => {
      if (preview) {
        return displayNode;
      }

      return (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'flex-start',
            gap: 0.75,
            minWidth: 0,
            cursor: 'pointer',
            borderRadius: 0.5,
            transition: 'background-color 120ms ease, opacity 120ms ease',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(148, 163, 184, 0.12)'
                : 'rgba(148, 163, 184, 0.14)',
            },
            '& .json-copy-action': {
              opacity: 0,
              pointerEvents: 'none',
            },
            '&:hover .json-copy-action': {
              opacity: 1,
            },
          }}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            handleCopyValue(valueToCopy, valuePath);
          }}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              handleCopyValue(valueToCopy, valuePath);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={copiedValuePath === valuePath ? 'Copied' : 'Click to copy value'}
        >
          {displayNode}
          <Box
            component="span"
            className="json-copy-action"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              alignSelf: 'flex-start',
              gap: 0.4,
              p: 0,
              m: 0,
              backgroundColor: 'transparent',
              color: copiedValuePath === valuePath ? theme.palette.success.main : theme.palette.text.secondary,
              fontSize: '11px',
              transition: 'opacity 120ms ease',
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily,
                fontSize: '11px',
                color: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              {copiedValuePath === valuePath ? 'Copied' : 'Click to copy'}
            </Typography>
            <ContentCopyIcon sx={{ fontSize: '12px' }} />
          </Box>
        </Box>
      );
    };

    if (value === undefined) {
      const displayNode = (
        <Typography
          component="span"
          sx={{
            fontFamily,
            color: preview ? previewValueColor : mutedColor,
            fontSize,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          undefined
        </Typography>
      );
      return renderValueWithCopy(displayNode, 'undefined');
    }
    if (value === null) {
      const displayNode = (
        <Typography
          component="span"
          sx={{
            fontFamily,
            color: preview ? previewValueColor : nullColor,
            fontSize,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          null
        </Typography>
      );
      return renderValueWithCopy(displayNode, 'null');
    }
    const valueType = typeof value;

    if (valueType === 'string') {
      const str = value as string;
      if (preview && str.length > 40) {
        return (
          <Typography
            component="span"
            sx={{ fontFamily, color: preview ? previewValueColor : stringColor, fontSize, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
          >
            {`"${str.slice(0, 40)}…"`}
          </Typography>
        );
      }
      const canExpandFullString = Boolean(valuePath) && !preview;
      const stringTruncation = getTruncatedString(
        str,
        Math.max(1, maxTruncatedStringChars),
      );
      const shouldShowFullString = canExpandFullString ? isStringExpanded(valuePath as string) : true;
      const displayValue = shouldShowFullString ? str : stringTruncation.text;
      const shouldShowToggle = canExpandFullString && stringTruncation.isTruncated;
      const displayNode = (
        <Box component="span" sx={{ display: 'inline', minWidth: 0 }}>
          <Typography
            component="span"
            sx={{
              fontFamily,
              color: preview ? previewValueColor : stringColor,
              fontSize,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {JSON.stringify(displayValue)}
          </Typography>
          {shouldShowToggle ? (
            <Typography
              component="button"
              type="button"
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                toggleStringExpanded(valuePath as string);
              }}
              sx={{
                ml: 0.75,
                border: 'none',
                p: 0,
                backgroundColor: 'transparent',
                fontFamily,
                fontSize: '11px',
                color: theme.palette.primary.main,
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': {
                  color: theme.palette.primary.dark,
                },
              }}
            >
              {shouldShowFullString ? 'Show less' : 'Show more'}
            </Typography>
          ) : null}
        </Box>
      );
      return renderValueWithCopy(displayNode, str);
    }

    if (valueType === 'number') {
      const displayNode = (
        <Typography
          component="span"
          sx={{ fontFamily, color: preview ? previewValueColor : numberColor, fontSize, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
        >
          {String(value)}
        </Typography>
      );
      return renderValueWithCopy(displayNode, String(value));
    }

    if (valueType === 'boolean') {
      const displayNode = (
        <Typography
          component="span"
          sx={{ fontFamily, color: preview ? previewValueColor : booleanColor, fontSize, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
        >
          {value ? 'true' : 'false'}
        </Typography>
      );
      return renderValueWithCopy(displayNode, value ? 'true' : 'false');
    }

    // fallback for other primitives (e.g. bigint)
    const displayNode = (
      <Typography
        component="span"
        sx={{ fontFamily, color: preview ? previewValueColor : valueColor, fontSize, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
      >
        {formatPrimitive(value)}
      </Typography>
    );
    return renderValueWithCopy(displayNode, formatPrimitive(value));
  };

  const renderPreviewContent = (value: unknown, maxProps = 2) => {
    if (Array.isArray(value)) {
      const first = value[0];
      if (first === undefined) return null;
      if (first === null || ['string', 'number', 'boolean'].includes(typeof first)) {
        return renderPrimitiveValue(first, { preview: true });
      }
      // nested object/array preview placeholder
      const placeholder = Array.isArray(first) ? '[…]' : '{…}';
      return (
        <Typography component="span" sx={{ fontFamily, color: previewValueColor, fontSize: '11px' }}>
          {placeholder}
        </Typography>
      );
    }

    if (typeof value === 'object' && value) {
      const obj = value as Record<string, unknown>;
      const keys = Object.keys(obj).sort().slice(0, maxProps);
      if (keys.length === 0) return null;

      return (
        <Box component="span" sx={{ display: 'inline' }}>
          {keys.map((k, idx) => {
            const v = obj[k];
            const isPrimitive = v === null || ['string', 'number', 'boolean'].includes(typeof v);
            return (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`${k}-${idx}`}
                component="span"
                sx={{ display: 'inline' }}
              >
                    <Typography component="span" sx={{ fontFamily, color: previewValueColor, fontSize: '11px' }}>
                  {k}:{' '}
                </Typography>
                {isPrimitive ? (
                  renderPrimitiveValue(v, { preview: true })
                ) : (
                  <Typography component="span" sx={{ fontFamily, color: previewValueColor, fontSize: '11px', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                    {Array.isArray(v) ? '[…]' : '{…}'}
                  </Typography>
                )}
                {idx < keys.length - 1 ? (
                  <Typography component="span" sx={{ fontFamily, color: previewValueColor, fontSize: '11px' }}>
                    ,{' '}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Box>
      );
    }

    return null;
  };

  const renderNode = useCallback(
    (value: unknown, path: string, label?: string | number) => {
      const expandable = isExpandable(value);
      const expanded = isExpanded(path);

      const keyText =
        label === undefined ? null : typeof label === 'number' ? String(label) : String(label);

      if (!expandable) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
            {/* spacer for icon alignment */}
            <Box sx={{ width: 26, flexShrink: 0 }} />
            {keyText !== null && (
              <Typography
                component="span"
                sx={{
                  fontFamily,
                  color: keyColor,
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {keyText}:{' '}
              </Typography>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {renderPrimitiveValue(value, { valuePath: path })}
            </Box>
          </Box>
        );
      }

      const isArr = Array.isArray(value);
      const openChar = isArr ? '[' : '{';
      const closeChar = isArr ? ']' : '}';
      const previewContent = renderPreviewContent(value);

      const childRenderer = () => {
        if (Array.isArray(value)) {
          return value.map((child, index) => (
            <Box key={index} sx={{ mt: 0.03 }}>
              {renderNode(child, buildJsonPath(path, index), index)}
            </Box>
          ));
        }

        return Object.keys(value as Record<string, unknown>)
          .sort()
          .map(key => (
            <Box key={key} sx={{ mt: 0.03 }}>
              {renderNode(
                (value as Record<string, unknown>)[key],
                buildJsonPath(path, key),
                key,
              )}
            </Box>
          ));
      };

      return (
        <Box
          sx={{
            borderLeft: `2px solid ${neutralBorder}`,
            borderRadius: 1,
            backgroundColor: neutralBg,
            px: 0.5,
            py: expanded ? 0.25 : 0,
            width: '100%',
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
            <IconButton
              size="small"
              onClick={() => toggleExpanded(path)}
              sx={{
                p: 0.25,
                color: arrowColor,
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: theme.palette.action.hover },
              }}
              aria-label={expanded ? 'Collapse JSON node' : 'Expand JSON node'}
            >
              {expanded ? (
                <TriangleArrow direction="down" color={arrowColor} />
              ) : (
                <TriangleArrow direction="right" color={arrowColor} />
              )}
            </IconButton>

            {keyText !== null && (
              <Typography
                component="span"
                sx={{
                  fontFamily,
                  color: keyColor,
                  fontSize: '12px',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  fontWeight: 700,
                }}
              >
                {keyText}:{' '}
              </Typography>
            )}

            <Typography
              component="span"
              sx={{
                fontFamily,
                // Brackets shown after the key (inline preview style) should match the light preview color.
                color: previewValueColor,
                fontSize: '12px',
                fontWeight: 600,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                flex: 1,
                minWidth: 0,
              }}
            >
              {openChar}
              {previewContent ? (
                <Box component="span" sx={{ display: 'inline' }}>
                  {expanded ? ' ' : ' '}
                  {previewContent}
                </Box>
              ) : null}
              {/* Always show close bracket on the same inline line (even when expanded). */}
              {closeChar}
            </Typography>
          </Box>

          {expanded && <Box sx={{ pl: 0.55, mt: 0.01 }}>{childRenderer()}</Box>}
        </Box>
      );
    },
    [
      fontFamily,
      isExpanded,
      keyColor,
      mutedColor,
      previewValueColor,
      toggleExpanded,
      valueColor,
      theme.palette.action.hover,
      neutralBg,
      neutralBorder,
      renderPrimitiveValue,
      renderPreviewContent,
      isStringExpanded,
      toggleStringExpanded,
      maxTruncatedStringChars,
    ],
  );

  const resizeCursor = canResizeWidth && canResizeHeight
    ? 'nwse-resize'
    : canResizeWidth
      ? 'ew-resize'
      : 'ns-resize';

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-block',
        overflow: 'visible',
        verticalAlign: 'top',
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          p: 0,
          borderRadius: 1,
          backgroundColor: (isResizeHandleHovered || isResizing)
            ? theme.palette.mode === 'dark'
              ? 'rgba(148, 163, 184, 0.16)'
              : 'rgba(148, 163, 184, 0.18)'
            : 'transparent',
          border: isResizing
            ? `1px dotted ${theme.palette.primary.main}`
            : '1px solid transparent',
          width: viewerWidth === 'auto' ? 'fit-content' : `${viewerWidth}px`,
          maxWidth: viewerWidth === 'auto' ? '100%' : '95vw',
          minWidth: viewerWidth === 'auto' ? 'unset' : `${minWidth}px`,
          height: viewerHeight === 'auto' ? 'auto' : `${viewerHeight}px`,
          minHeight: viewerHeight === 'auto' ? 'unset' : `${minHeight}px`,
          overflowX: 'hidden',
          overflowY: viewerHeight === 'auto' ? 'visible' : 'auto',
          WebkitOverflowScrolling: 'touch',
          userSelect: isResizing ? 'none' : 'auto',
          transition: 'background-color 160ms ease',
          pr: 0,
          pb: 0,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(148, 163, 184, 0.08)'
              : 'rgba(148, 163, 184, 0.07)',
          },
          ...((sx as object) ?? {}),
        }}
      >
        <Box ref={contentRef}>
          {isExpandable(data) ? (
            renderNode(data, 'root')
          ) : (
            <Typography sx={{ fontFamily, color: valueColor, fontSize: '12px' }}>
              {formatPrimitive(data)}
            </Typography>
          )}
        </Box>
      </Box>

      {showResizeHandle ? (
        <Box
          onMouseDown={handleResizeStart}
          onMouseEnter={() => setIsResizeHandleHovered(true)}
          onMouseLeave={() => setIsResizeHandleHovered(false)}
          sx={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            cursor: resizeCursor,
            opacity: 0.9,
            borderRadius: 0.75,
            zIndex: 2,
            backgroundColor: 'transparent',
            border: 'none',
            '&:hover': {
              opacity: 1,
              backgroundColor: 'transparent',
            },
          }}
          aria-label="Resize JSON viewer"
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRight: `2px solid ${theme.palette.text.secondary}`,
              borderBottom: `2px solid ${theme.palette.text.secondary}`,
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default JsonObjectViewer;

